import { ScheduleDTO } from "@/dto/schedule";
import { ReservationMutationPayload } from "@/dto/mutation";
import axios, { AxiosError } from "axios";
import { ProfileDTO } from "@/dto/profile";
import { LogsUrlDTO } from "@/dto/logsUrl";
import { Msg } from "@/services/PusherService";

import { BaseService } from "../base";
import { httpClient as createHttpClient } from "@/services/api";
import { deviceTimeZone } from "@/helpers/Time";

const pingHttpClient = createHttpClient({ enableRetry: false });

class HttpService extends BaseService {
  async getSchedule(): Promise<{ error: AxiosError | null; schedule: ScheduleDTO | null }> {
    const { error, data } = await this.axiosCall<{
      schedule: ScheduleDTO;
    }>({
      method: "get",
      url: `api/schedule`,
      params: { time_zone: deviceTimeZone() },
    });
    if (!data) return { error, schedule: null };
    return { error, schedule: data.schedule };
  }

  pairDevice(pinCode: number[]): Promise<{ error: AxiosError | null; data?: null }> {
    return this.axiosCall<null>({ method: "post", url: "/api/pairing", data: { pin: pinCode.join("") } });
  }

  getProfile(): Promise<{ error: AxiosError | null; data?: { profile: ProfileDTO } | null }> {
    return this.axiosCall<{ profile: ProfileDTO }>({ method: "get", url: "/api/profile" });
  }

  getSendLogsUri(fileName: string): Promise<{ error: AxiosError | null; data?: LogsUrlDTO | null }> {
    return this.axiosCall<LogsUrlDTO>({ method: "get", url: "/api/presigned_url", params: { object_name: fileName } });
  }

  sendLogs(uri: string, logs: File) {
    return axios.put(uri, logs);
  }

  pusherAck(message: Msg) {
    return axios.post("/pusher/ack", { msgId: message.msgId });
  }

  endReservation(
    reservationId: number
  ): Promise<{ error: AxiosError | null; data?: ReservationMutationPayload | null }> {
    return this.axiosCall<ReservationMutationPayload>({ method: "post", data: { reservationId }, url: "/api/end_now" });
  }

  useNowResource(tz: string): Promise<{ error: AxiosError | null; data?: ReservationMutationPayload | null }> {
    return this.axiosCall<ReservationMutationPayload>({ method: "post", data: { tz }, url: "/api/use_now" });
  }

  checkIn(reservationId: number): Promise<{ error: AxiosError | null; data?: ReservationMutationPayload | null }> {
    return this.axiosCall<ReservationMutationPayload>({
      method: "POST",
      url: "/api/check_in",
      data: { reservationId },
    });
  }

  addTime(
    reservationId: number,
    minutes: number
  ): Promise<{ error: AxiosError | null; data?: ReservationMutationPayload | null }> {
    return this.axiosCall<ReservationMutationPayload>({
      method: "POST",
      url: "/api/add_time",
      data: { reservationId, minutes },
    });
  }

  async ping(url: string): Promise<{ error: AxiosError | null; data?: { reload?: boolean } | null }> {
    try {
      const { data } = await pingHttpClient.request<{ reload?: boolean }>({ method: "PUT", url });
      return { error: null, data };
    } catch (error) {
      return { error: error as AxiosError, data: null };
    }
  }

  pingAck(url: string): Promise<{ error: AxiosError | null }> {
    return this.axiosCall({ method: "POST", url });
  }
}

export const httpService = new HttpService();
export default httpService;
